import { Routes, Route } from 'react-router-dom'
import { FeedbackProvider } from '@/components/ui/feedback'

// Layouts
import MainLayout from './layouts/MainLayout'

// Pages — statically imported (NOT React.lazy): the prerenderer renders through
// renderToString, which is synchronous and cannot await a lazy() chunk, so lazy
// routes would SSR the Suspense fallback instead of real content. Static imports
// keep every route crawlable. See docs/conventions/frontend.md + scripts/prerender-bespoke.mjs.
import HomePage from './pages/HomePage'
import AboutPage from './pages/AboutPage'
import ContactPage from './pages/ContactPage'
import GetStartedPage from './pages/GetStartedPage'
import BookPage from './pages/BookPage'
import CancelBookingPage from './pages/CancelBookingPage'
import WhoWeHelpPage from './pages/WhoWeHelpPage'
import HowWeMeasurePage from './pages/HowWeMeasurePage'
import PayPage from './pages/PayPage'
import LinkInBioPage from './pages/LinkInBioPage'
import ChecklistPage from './pages/ChecklistPage'
import RequestsPage from './pages/RequestsPage'
import WelcomePage from './pages/WelcomePage'
import CallPage from './pages/CallPage'

// Packages
import PackagesPage from './pages/packages/PackagesPage'

// Portfolio
import PortfolioPage from './pages/portfolio/PortfolioPage'
import PortfolioItemPage from './pages/portfolio/PortfolioItemPage'

// Blog
import BlogPage from './pages/blog/BlogPage'
import BlogPostPage from './pages/blog/BlogPostPage'
import ChromaFestivalCaseStudyPage from './pages/blog/ChromaFestivalCaseStudyPage'
import FestivalsLosingApplicantsPage from './pages/blog/FestivalsLosingApplicantsPage'
import SierraCanyonCaseStudyPage from './pages/blog/SierraCanyonCaseStudyPage'
import HolyTrinityCaseStudyPage from './pages/blog/HolyTrinityCaseStudyPage'
import BrassCityJazzCaseStudyPage from './pages/blog/BrassCityJazzCaseStudyPage'
import WendellWhalumCaseStudyPage from './pages/blog/WendellWhalumCaseStudyPage'
import BestWebsitePlatformsPage from './pages/blog/BestWebsitePlatformsPage'

// Services
import ServicesPage from './pages/services/ServicesPage'
import FestivalRetainerPage from './pages/services/FestivalRetainerPage'
import DigitalSystemsPage from './pages/services/DigitalSystemsPage'
import ApplicationFunnelsPage from './pages/services/ApplicationFunnelsPage'
import FestivalWebsitesPage from './pages/services/FestivalWebsitesPage'
import WebsitesPage from './pages/services/WebsitesPage'
import DigitalPartnershipPage from './pages/services/DigitalPartnershipPage'
import DoneForYouPage from './pages/services/DoneForYouPage'
import MusicManagerPage from './pages/MusicManagerPage'
import EmailMarketingPage from './pages/services/EmailMarketingPage'
import DonorManagementPage from './pages/services/DonorManagementPage'
import TicketingPage from './pages/services/TicketingPage'
import GraphicDesignPage from './pages/services/GraphicDesignPage'
import PhotographyPage from './pages/services/PhotographyPage'

// Platform
import PlatformPage from './pages/platform/PlatformPage'
import DonationsFeaturePage from './pages/platform/features/DonationsFeaturePage'
import WebsiteFeaturePage from './pages/platform/features/WebsiteFeaturePage'
import TeamFeaturePage from './pages/platform/features/TeamFeaturePage'
import EventsFeaturePage from './pages/platform/features/EventsFeaturePage'
import EmailFeaturePage from './pages/platform/features/EmailFeaturePage'
import AnalyticsFeaturePage from './pages/platform/features/AnalyticsFeaturePage'

// Who we serve (audience segments; /who-we-help remains the index)
import MusicFestivalsPage from './pages/who-we-help/MusicFestivalsPage'
import ChoralFestivalsPage from './pages/who-we-help/ChoralFestivalsPage'
import YoungArtistProgramsPage from './pages/who-we-help/YoungArtistProgramsPage'
import PreCollegeSummerProgramsPage from './pages/who-we-help/PreCollegeSummerProgramsPage'
import ChurchesSacredMusicPage from './pages/who-we-help/ChurchesSacredMusicPage'
import MusicSchoolsPage from './pages/who-we-help/MusicSchoolsPage'

// Compare
import CompareIndexPage from './pages/compare/CompareIndexPage'
import AcceptdComparePage from './pages/compare/AcceptdComparePage'
import SquarespaceComparePage from './pages/compare/SquarespaceComparePage'
import WixComparePage from './pages/compare/WixComparePage'
import WordPressComparePage from './pages/compare/WordPressComparePage'
import EventbriteComparePage from './pages/compare/EventbriteComparePage'

// Resources
import ResourcesPage from './pages/resources/ResourcesPage'

// Legal
import PrivacyPage from './pages/PrivacyPage'
import AccessibilityPage from './pages/AccessibilityPage'

// 404
import NotFoundPage from './pages/NotFoundPage'

// The Router is provided by the harness, NOT here: mountClient() wraps
// <BrowserRouter> on the client and createRenderBody() wraps <StaticRouter> on
// the server (both from @gjl/site-kit). App must stay router-agnostic so the
// same tree renders in both. injectedData is part of the @gjl/site-kit App
// contract; this marketing site carries no CMS payload, so it is ignored.
function App(_props: { injectedData?: unknown } = {}) {
  return (
    <FeedbackProvider>
      <Routes>
        {/* Standalone pages (no nav) */}
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/call" element={<CallPage />} />

        <Route element={<MainLayout />}>
          {/* Main Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/get-started" element={<GetStartedPage />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/book/cancel" element={<CancelBookingPage />} />
          <Route path="/who-we-help" element={<WhoWeHelpPage />} />
          <Route path="/who-we-help/music-festivals" element={<MusicFestivalsPage />} />
          <Route path="/who-we-help/choral-festivals" element={<ChoralFestivalsPage />} />
          <Route path="/who-we-help/young-artist-programs" element={<YoungArtistProgramsPage />} />
          <Route path="/who-we-help/pre-college-summer-programs" element={<PreCollegeSummerProgramsPage />} />
          <Route path="/who-we-help/churches-sacred-music" element={<ChurchesSacredMusicPage />} />
          <Route path="/who-we-help/music-schools" element={<MusicSchoolsPage />} />

          {/* Compare */}
          <Route path="/compare" element={<CompareIndexPage />} />
          <Route path="/compare/acceptd" element={<AcceptdComparePage />} />
          <Route path="/compare/squarespace" element={<SquarespaceComparePage />} />
          <Route path="/compare/wix" element={<WixComparePage />} />
          <Route path="/compare/wordpress" element={<WordPressComparePage />} />
          <Route path="/compare/eventbrite" element={<EventbriteComparePage />} />
          <Route path="/how-we-measure" element={<HowWeMeasurePage />} />
          <Route path="/pay" element={<PayPage />} />
          <Route path="/link-in-bio" element={<LinkInBioPage />} />
          <Route path="/checklist" element={<ChecklistPage />} />
          <Route path="/requests" element={<RequestsPage />} />

          {/* Packages */}
          <Route path="/packages" element={<PackagesPage />} />

          {/* Portfolio */}
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/portfolio/:slug" element={<PortfolioItemPage />} />

          {/* Blog */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/chroma-festival-case-study" element={<ChromaFestivalCaseStudyPage />} />
          <Route path="/blog/festivals-losing-applicants" element={<FestivalsLosingApplicantsPage />} />
          <Route path="/blog/sierra-canyon-case-study" element={<SierraCanyonCaseStudyPage />} />
          <Route path="/blog/holy-trinity-case-study" element={<HolyTrinityCaseStudyPage />} />
          <Route path="/blog/brass-city-jazz-case-study" element={<BrassCityJazzCaseStudyPage />} />
          <Route path="/blog/wendell-whalum-case-study" element={<WendellWhalumCaseStudyPage />} />
          <Route path="/blog/best-website-platforms-for-music-festivals" element={<BestWebsitePlatformsPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Services */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/festival-retainer" element={<FestivalRetainerPage />} />
          <Route path="/services/digital-systems" element={<DigitalSystemsPage />} />
          <Route path="/services/application-funnels" element={<ApplicationFunnelsPage />} />
          <Route path="/services/festival-websites" element={<FestivalWebsitesPage />} />
          <Route path="/services/websites" element={<WebsitesPage />} />
          <Route path="/services/digital-partnership" element={<DigitalPartnershipPage />} />
          <Route path="/services/done-for-you" element={<DoneForYouPage />} />
          <Route path="/music-manager" element={<MusicManagerPage />} />
          {/* Alias, not a redirect: <Navigate> cannot SSR under the prerenderer; the page's canonical points at /music-manager. */}
          <Route path="/canticle" element={<MusicManagerPage />} />
          <Route path="/services/email-marketing" element={<EmailMarketingPage />} />
          <Route path="/services/donor-management" element={<DonorManagementPage />} />
          <Route path="/services/ticketing" element={<TicketingPage />} />
          <Route path="/services/design" element={<GraphicDesignPage />} />
          <Route path="/services/photography" element={<PhotographyPage />} />

          {/* Platform */}
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="/platform/features/donations" element={<DonationsFeaturePage />} />
          <Route path="/platform/features/website" element={<WebsiteFeaturePage />} />
          <Route path="/platform/features/team" element={<TeamFeaturePage />} />
          <Route path="/platform/features/events" element={<EventsFeaturePage />} />
          <Route path="/platform/features/email" element={<EmailFeaturePage />} />
          <Route path="/platform/features/analytics" element={<AnalyticsFeaturePage />} />

          {/* Resources */}
          <Route path="/resources" element={<ResourcesPage />} />

          {/* Legal */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </FeedbackProvider>
  )
}

export default App
